import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { API_BASE_URL } from "../../config";
import { configurationEditEnabled } from "../../config/productFeatures";
import { createIntakeSession } from "../../services/intakeClient";
import { resolveGate0, saveGate0 } from "../../utils/gate0Storage";
import { resolveIntakeStartedAt } from "../../utils/intakeStartedStorage";
import { getPresetFramework } from "./presets";
import {
  normalizeFramework,
  createSection,
  validateFramework,
  frameworkForSave,
  uniqueSectionId,
  assignUniqueItemIds,
} from "./frameworkUtils";
import {
  cloneSectionForDrawer,
  drawerDraftToSectionPatch,
} from "./sectionEditorUtils";
import SectionEditorDrawer from "./SectionEditorDrawer";
import DeleteSectionDialog from "./DeleteSectionDialog";
import ConfigurationTable from "./ConfigurationTable";
import IntakeHeaderBackButton from "../../components/intake/IntakeHeaderBackButton";
import IntakeSuccessToast from "../../components/intake/IntakeSuccessToast";
import { mergeFixedFrSections, isFixedFrSection } from "./frElicitationSections";

/** Same 24px / 16px back-arrow ink placement as Gate 0, Gate 1, and summary. */
const BACK_ARROW_INK_LEFT_PX = 24;
const BACK_ARROW_INK_GAP_RIGHT_PX = 16;
const BACK_ARROW_HIT_TARGET_INSET_PX = 6;
const BACK_ARROW_BUTTON_SIZE_PX = 32;
const TITLE_LEFT_SIDE_BEARING_PX = 1;
const HEADER_INSET_LEFT_PX = BACK_ARROW_INK_LEFT_PX - BACK_ARROW_HIT_TARGET_INSET_PX;
const BACK_ARROW_GAP_RIGHT_PX =
  BACK_ARROW_INK_GAP_RIGHT_PX - BACK_ARROW_HIT_TARGET_INSET_PX - TITLE_LEFT_SIDE_BEARING_PX;
const HEADER_ROW_GAP_PX = 8;
const TITLE_NUDGE_LEFT_PX = BACK_ARROW_GAP_RIGHT_PX - HEADER_ROW_GAP_PX;
const HEADER_TEXT_COLUMN_INSET_PX = BACK_ARROW_BUTTON_SIZE_PX + BACK_ARROW_GAP_RIGHT_PX;

const C = {
  pageBg: "#F5F5F5",
  surface: "#FFFFFF",
  border: "#E6E6E6",
  primary: "#005BF0",
  primary20: "#F0F6FF",
  text: "#383838",
  muted: "#707070",
  headerBg: "#FFFFFF",
};

const appNameHeadingSx = {
  width: "100%",
  minHeight: 24,
  height: "auto",
  display: "flex",
  alignItems: "flex-start",
  gap: 0.5,
  mt: 0.5,
  opacity: 1,
  fontFamily: "Lato, sans-serif",
  fontWeight: 400,
  fontStyle: "normal",
  fontSize: 14,
  lineHeight: "24px",
  letterSpacing: 0,
  color: "#383838",
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
};

const PRESET_LINKS = [
  { key: "lite", label: "Lightweight" },
  { key: "standard", label: "Standard" },
  { key: "domain", label: "Healthcare" },
  { key: "deep", label: "Deep Enterprise" },
];

const PRESET_BTN_SHADOW = "0px 1px 2px 0px rgba(0, 0, 0, 0.04)";

const quickPresetBtnSx = {
  textTransform: "none",
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 13,
  lineHeight: "24px",
  letterSpacing: 0,
  color: C.primary,
  minWidth: 127,
  width: "auto",
  height: 32,
  minHeight: 32,
  py: "4px",
  px: "16px",
  gap: "4px",
  m: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  verticalAlign: "middle",
  borderRadius: "4px",
  boxSizing: "border-box",
  bgcolor: "#FFFFFF",
  border: `1px solid ${C.primary}`,
  boxShadow: PRESET_BTN_SHADOW,
  "&:hover": {
    bgcolor: "#005BF014",
    color: C.primary,
    border: `1px solid ${C.primary}`,
    boxShadow: PRESET_BTN_SHADOW,
  },
  "&:focus": {
    bgcolor: "#FFFFFF",
    color: C.primary,
    border: "2px solid #005FCC",
    boxShadow: PRESET_BTN_SHADOW,
    outline: "none",
  },
  "&:focus-visible": {
    bgcolor: "#FFFFFF",
    color: C.primary,
    border: "2px solid #005FCC",
    boxShadow: PRESET_BTN_SHADOW,
    outline: "none",
  },
  "&:active": {
    bgcolor: "#FFFFFF",
    color: C.primary,
    border: `1px solid ${C.primary}`,
    boxShadow: PRESET_BTN_SHADOW,
  },
};

/** Outlined action button — same states as quick presets, sized for header actions. */
const customizeTemplateBtnSx = {
  ...quickPresetBtnSx,
  fontSize: 14,
  fontWeight: 600,
  minWidth: "auto",
  height: 40,
  minHeight: 40,
  py: "8px",
  whiteSpace: "nowrap",
  flexShrink: 0,
  "&:hover:not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "#F0F6FF",
    backgroundColor: "#F0F6FF",
    backgroundImage: "none",
    color: C.primary,
    border: `1px solid ${C.primary}`,
    boxShadow: PRESET_BTN_SHADOW,
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    color: C.primary,
    border: "2px solid #005BF0",
    boxShadow: PRESET_BTN_SHADOW,
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "2px solid #005BF0",
    color: C.primary,
  },
  "& .MuiTouchRipple-root": { display: "none" },
};

const footerBackBtnSx = {
  textTransform: "none",
  fontWeight: 600,
  borderColor: C.primary,
  color: C.primary,
  borderRadius: "4px",
  minWidth: 88,
  height: 40,
  bgcolor: "#FFFFFF",
  backgroundImage: "none",
  boxShadow: PRESET_BTN_SHADOW,
  "&:hover:not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "#F0F6FF",
    backgroundColor: "#F0F6FF",
    backgroundImage: "none",
    color: C.primary,
    border: `1px solid ${C.primary}`,
    boxShadow: PRESET_BTN_SHADOW,
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    color: C.primary,
    border: "2px solid #005BF0",
    boxShadow: PRESET_BTN_SHADOW,
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "2px solid #005BF0",
    color: C.primary,
  },
  "& .MuiTouchRipple-root": { display: "none" },
};

const footerNextBtnSx = {
  textTransform: "none",
  fontWeight: 600,
  bgcolor: C.primary,
  backgroundImage: "none",
  color: "#FFFFFF",
  borderRadius: "4px",
  minWidth: 88,
  height: 40,
  border: "1px solid transparent",
  boxShadow: "none",
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "#0046B8",
    backgroundColor: "#0046B8",
    backgroundImage: "none",
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: `${C.primary} !important`,
    backgroundColor: `${C.primary} !important`,
    backgroundImage: "none !important",
    color: "#FFFFFF",
    border: "2px solid #005BF0",
    boxShadow: "0 0 0 2px #FFFFFF, 0 0 0 4px #005BF0",
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: `${C.primary} !important`,
    backgroundColor: `${C.primary} !important`,
    backgroundImage: "none !important",
    color: "#FFFFFF",
    border: "2px solid #005BF0",
    boxShadow: "0 0 0 2px #FFFFFF, 0 0 0 4px #005BF0",
  },
  "& .MuiTouchRipple-root": { display: "none" },
  "&.Mui-disabled": {
    bgcolor: C.primary,
    color: "#FFFFFF",
    opacity: 0.6,
  },
};

export default function FrameworkDesignerPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const gate0 = useMemo(
    () => resolveGate0(projectId, location.state),
    [projectId, location.state]
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [autoSaved, setAutoSaved] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [project, setProject] = useState(null);
  const [framework, setFramework] = useState(null);
  const [frameworkEditable, setFrameworkEditable] = useState(true);
  const [frameworkSource, setFrameworkSource] = useState("project");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("add");
  const [drawerReadOnly, setDrawerReadOnly] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [drawerDraft, setDrawerDraft] = useState(null);
  const [originalSectionName, setOriginalSectionName] = useState("");

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState(null);

  useEffect(() => {
    if (gate0 && projectId) saveGate0(projectId, gate0);
  }, [projectId, gate0]);

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/projects/${projectId}/framework`);
        if (res.data?.success) {
          setProject({
            id: res.data.data.project_id,
            project_name: res.data.data.project_name,
            status: res.data.data.status,
          });
          setFrameworkEditable(res.data.data.framework_editable !== false);
          setFrameworkSource(res.data.data.framework_source || "project");
          const loaded = res.data.data.framework
            ? normalizeFramework(res.data.data.framework)
            : getPresetFramework("standard");
          // Fixed FR-elicitation sections are always merged in (locked, non-removable).
          setFramework(mergeFixedFrSections(loaded));
        } else {
          setError("Could not load project.");
        }
      } catch (e) {
        setError(e.response?.data?.error || "Failed to load framework.");
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId]);

  const autoSave = useCallback(async (updatedFramework, { allowWhenEditDisabled = false } = {}) => {
    if (!frameworkEditable) return false;
    // Block Add/Edit-originated saves when the feature flag is off.
    // Delete may still persist by passing allowWhenEditDisabled: true.
    if (!configurationEditEnabled() && !allowWhenEditDisabled) return false;
    const validationError = validateFramework(updatedFramework);
    if (validationError) return false;
    try {
      setAutoSaving(true);
      setAutoSaved(false);
      const payload = frameworkForSave(updatedFramework);
      const res = await axios.put(`${API_BASE_URL}/projects/${projectId}/framework`, {
        framework: payload,
      });
      if (res.data?.success) {
        setAutoSaved(true);
        setTimeout(() => setAutoSaved(false), 3000);
        return true;
      }
      return false;
    } catch {
      // silent — user still sees the change in UI; "Save & Continue" is the hard save
      return false;
    } finally {
      setAutoSaving(false);
    }
  }, [projectId, frameworkEditable]);

  const applyPreset = (key) => {
    if (!configurationEditEnabled() || !frameworkEditable) return;
    setFramework(mergeFixedFrSections(getPresetFramework(key)));
  };

  const openAddDrawer = () => {
    if (!configurationEditEnabled() || !frameworkEditable) return;
    const sec = createSection(framework?.sections || []);
    setDrawerMode("add");
    setEditingIndex(null);
    setOriginalSectionName("");
    setDrawerReadOnly(false);
    setDrawerDraft(cloneSectionForDrawer(sec));
    setDrawerOpen(true);
  };

  const openEditDrawer = (index) => {
    const sec = framework.sections[index];
    if (isFixedFrSection(sec) || !frameworkEditable || !configurationEditEnabled()) {
      openViewDrawer(index);
      return;
    }
    setDrawerMode("edit");
    setEditingIndex(index);
    setOriginalSectionName(sec.name || "");
    setDrawerReadOnly(false);
    setDrawerDraft(cloneSectionForDrawer(sec));
    setDrawerOpen(true);
  };

  // Fixed FR sections are view-only: open the drawer read-only so users can see
  // (but not change) the questions the chatbot will ask.
  const openViewDrawer = (index) => {
    const sec = framework.sections[index];
    setDrawerMode("edit");
    setEditingIndex(index);
    setOriginalSectionName(sec.name || "");
    setDrawerReadOnly(true);
    setDrawerDraft(cloneSectionForDrawer(sec));
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerReadOnly(false);
    setDrawerDraft(null);
  };

  const commitDrawerDraft = useCallback(
    (draft) => {
      if (!configurationEditEnabled() || !frameworkEditable) return;
      let updated;
      if (drawerMode === "add") {
        setFramework((f) => {
          const ids = new Set(f.sections.map((s) => s.id));
          const globalItemIds = new Set(
            f.sections.flatMap((s) => (s.items || []).map((it) => it.id))
          );
          const patch = drawerDraftToSectionPatch(draft);
          patch.id = uniqueSectionId(patch.name, ids);
          patch.items = assignUniqueItemIds(patch.id, patch.items, globalItemIds).map(
            (it, i) => ({
              ...it,
              extraction_key: it.extraction_key || `${patch.id}_q${i + 1}`,
            })
          );
          updated = { ...f, sections: [...f.sections, patch] };
          return updated;
        });
      } else if (editingIndex !== null) {
        setFramework((f) => {
          const stableId = f.sections[editingIndex]?.id;
          const patch = drawerDraftToSectionPatch(draft, stableId);
          updated = {
            ...f,
            sections: f.sections.map((s, i) => (i === editingIndex ? { ...s, ...patch } : s)),
          };
          return updated;
        });
      }
      closeDrawer();
      // Auto-save after React state is flushed
      setTimeout(async () => {
        if (updated && (await autoSave(updated))) {
          setSuccess("Configuration changes saved successfully.");
        }
      }, 0);
    },
    [drawerMode, editingIndex, autoSave, frameworkEditable]
  );

  const handleDrawerSave = (draft) => {
    if (!configurationEditEnabled() || !frameworkEditable) return;
    commitDrawerDraft(draft);
  };

  const requestDeleteSection = (index) => {
    if (!configurationEditEnabled()) return;
    if ((framework?.sections?.length || 0) <= 1) return;
    if (isFixedFrSection(framework?.sections?.[index])) return; // fixed FR sections cannot be deleted
    setPendingDeleteIndex(index);
    setDeleteConfirmOpen(true);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setPendingDeleteIndex(null);
  };

  const handleConfirmDelete = () => {
    if (!configurationEditEnabled()) return;
    if (pendingDeleteIndex === null) return;
    let updated;
    setFramework((f) => {
      updated = { ...f, sections: f.sections.filter((_, i) => i !== pendingDeleteIndex) };
      return updated;
    });
    handleCancelDelete();
    setTimeout(() => {
      if (updated) autoSave(updated, { allowWhenEditDisabled: true });
    }, 0);
  };

  const handleSaveAndNext = async () => {
    const validationError = validateFramework(framework);
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      setSaving(true);
      setError("");
      // Next always persists current framework so onboarding can proceed (not an Add/Edit action).
      const payload = frameworkForSave(framework);
      const res = await axios.put(`${API_BASE_URL}/projects/${projectId}/framework`, {
        framework: payload,
      });
      if (!res.data?.success) {
        setError(res.data?.error || "Failed to save framework.");
        return;
      }

      const gate0Payload = resolveGate0(projectId, location.state);
      if (gate0Payload) saveGate0(projectId, gate0Payload);

      try {
        const created = await createIntakeSession(
          projectId,
          gate0Payload || { app_name: project?.project_name }
        );
        resolveIntakeStartedAt(projectId, {
          progressStartedAt: created?.progress?.started_at,
        });
      } catch (sessionErr) {
        const detail =
          sessionErr.response?.data?.error
          || sessionErr.response?.data?.detail
          || sessionErr.message;
        setError(
          typeof detail === "string"
            ? `Framework saved, but chat session could not start: ${detail}`
            : "Framework saved, but chat session could not start. Is the intake agent running?"
        );
        return;
      }

      navigate(`/chat/${projectId}`, { state: { gate0: gate0Payload } });
    } catch (e) {
      setError(e.response?.data?.error || "Failed to save framework.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: "100%" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!project || !framework) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">{error || "Could not load project."}</Typography>
      </Box>
    );
  }

  const sections = framework.sections || [];
  const structureLocked = !frameworkEditable;
  const editEnabled = configurationEditEnabled() && !structureLocked;

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: "100%",
        bgcolor: C.pageBg,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Breadcrumb */}
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: C.surface,
          borderBottom: `1px solid ${C.border}`,
          px: { xs: 2, md: 3 },
          py: 1.25,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <IconButton size="small" onClick={() => navigate("/home")} sx={{ p: 0.5 }}>
          <HomeOutlinedIcon sx={{ fontSize: 18, color: C.muted }} />
        </IconButton>
        <ChevronRightIcon sx={{ fontSize: 16, color: C.muted }} />
        <Typography sx={{ fontSize: 13, color: C.muted, fontWeight: 500 }}>Configuration</Typography>
      </Box>

      {/* Hero — white surface to match breadcrumb bar */}
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: C.surface,
          borderBottom: `1px solid ${C.border}`,
          pl: `${HEADER_INSET_LEFT_PX}px`,
          pr: { xs: 2, md: 3 },
          py: 2.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
            mb: 1,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: `${HEADER_ROW_GAP_PX}px`,
                minWidth: 0,
                mb: 1,
              }}
            >
              <IntakeHeaderBackButton onClick={() => navigate("/home")} aria-label="Back" />
              <Typography
                sx={{
                  ml: `${TITLE_NUDGE_LEFT_PX}px`,
                  flex: "1 1 auto",
                  minWidth: 0,
                  fontSize: 22,
                  fontWeight: 700,
                  color: C.text,
                  m: 0,
                }}
              >
                Configuration
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: 14,
                color: C.muted,
                lineHeight: 1.5,
                width: "100%",
                whiteSpace: "normal",
                overflowWrap: "break-word",
                pl: `${HEADER_TEXT_COLUMN_INSET_PX}px`,
                boxSizing: "border-box",
              }}
            >
              Manage sections, questions, confidence scores, and item counts. Add, edit, or remove
              sections and questions as needed.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            disableElevation
            disableRipple
            disableFocusRipple
            disabled={saving}
            onClick={() =>
              navigate(`/framework/${projectId}/customize-template`, {
                state: { gate0 },
              })
            }
            sx={customizeTemplateBtnSx}
          >
            Customize Template
          </Button>
        </Box>
        {structureLocked && (
          <Alert severity="info" sx={{ mt: 1.5, maxWidth: 720 }}>
            Framework structure is locked by your ValidAIte administrator
            {frameworkSource === "tenant_template" ? " (tenant onboarding template)" : ""}.
            You can review sections and continue to intake — the 52 FR sections remain visible below.
          </Alert>
        )}
        {gate0?.app_name && (
          <Typography component="div" sx={{ ...appNameHeadingSx, pl: `${HEADER_TEXT_COLUMN_INSET_PX}px` }}>
            <Box component="span" sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
              App Name:
            </Box>
            <Box
              component="span"
              sx={{
                fontWeight: 700,
                flex: 1,
                minWidth: 0,
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {gate0.app_name}
            </Box>
          </Typography>
        )}
        {configurationEditEnabled() && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mt: 1.5, pl: `${HEADER_TEXT_COLUMN_INSET_PX}px` }}>
            <Typography
              sx={{
                fontSize: 12,
                color: C.muted,
                fontWeight: 600,
                lineHeight: "24px",
                height: 32,
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              Quick preset:
            </Typography>
            {PRESET_LINKS.map((p) => (
              <Button
                key={p.key}
                variant="text"
                disableRipple
                disabled={!editEnabled}
                onClick={() => applyPreset(p.key)}
                sx={quickPresetBtnSx}
              >
                {p.label}
              </Button>
            ))}
          </Box>
        )}
      </Box>

      {/* Table */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", p: { xs: 2, md: 3 } }}>
        <ConfigurationTable
          sections={sections}
          structureLocked={structureLocked}
          editEnabled={editEnabled}
          onEdit={openEditDrawer}
          onView={openViewDrawer}
          onDelete={requestDeleteSection}
        />
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          flexShrink: 0,
          height: 56,
          boxSizing: "border-box",
          bgcolor: C.surface,
          borderTop: `1px solid ${C.border}`,
          px: "24px",
          py: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          boxShadow: "0px -1px 8px 0px #2122260A",
        }}
      >
        <Button
          variant="outlined"
          disableElevation
          disableRipple
          disableFocusRipple
          onClick={() => navigate("/home")}
          disabled={saving}
          sx={footerBackBtnSx}
        >
          Back
        </Button>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {autoSaving && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <CircularProgress size={13} sx={{ color: C.muted }} />
              <Typography sx={{ fontSize: 12, color: C.muted }}>Saving…</Typography>
            </Box>
          )}
          {autoSaved && !autoSaving && (
            <Typography sx={{ fontSize: 12, color: "#2E7D32", fontWeight: 600 }}>
              ✓ Saved
            </Typography>
          )}
          <Button
            variant="contained"
            disableElevation
            disableRipple
            disableFocusRipple
            disabled={saving}
            onClick={handleSaveAndNext}
            sx={footerNextBtnSx}
          >
            {saving ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Next"}
          </Button>
        </Box>
      </Box>

      <SectionEditorDrawer
        open={drawerOpen}
        mode={drawerMode}
        readOnly={drawerReadOnly || !configurationEditEnabled()}
        draft={drawerDraft}
        originalName={originalSectionName}
        onClose={closeDrawer}
        onSave={handleDrawerSave}
      />

      <DeleteSectionDialog
        open={deleteConfirmOpen && configurationEditEnabled()}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />

      <Snackbar
        open={Boolean(error)}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      </Snackbar>
 
      <IntakeSuccessToast
        open={Boolean(success)}
        message={success}
        onClose={() => setSuccess("")}
      />
    </Box>
  );
}
